// Presentation-only derivation from the public microsite projection. Do not read
// raw strategy artifacts, drafts, governance objects, or activation instructions.
// What the journey names (the campaign, its audience, the creative theme of
// each stage) is read by utils/campaignJourneySemantics.js.
import {
  CREATIVE_THEME_ROLE,
  classifyCreativeThemeRole,
  isCreativeExecutionCopy,
  isTemplateCreativeSummary,
  resolveCampaignJourneyIdentity,
  resolveJourneyAudience,
} from "./campaignJourneySemantics";

/**
 * @typedef {{title: string, subtitle?: string, supportingLabel?: string, icon?: string}} FunnelStage
 * @typedef {{headline?: string, stages: FunnelStage[], bottomJourney?: string[], endGoalLabel?: string}} StrategyFunnelData
 */
const object = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const list = (value) => Array.isArray(value) ? value : [];

const text = (value, limit = 200) => {
  if (typeof value !== "string") return "";
  const clean = value.slice(0, 8000)
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*#`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean || /^(?:n\/?a|null|undefined|tbd|none|not (?:set|specified|available))\.?$/i.test(clean)) return "";
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).replace(/\s+\S*$/, "").trim()}…`;
};

const firstText = (...values) => values.map((value) => text(value)).find(Boolean) || "";
// A title whose first word is plain lower case gets its capital; a word with
// its own casing ("eBay") is left as written.
const sentenceCaseTitle = (value) =>
  typeof value === "string" && /^[a-z][a-z'’-]*(?:\s|$)/.test(value) ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
const ICONS = new Set(["awareness", "engagement", "ticket", "purchase", "lead", "traffic", "signup", "action"]);
const DEFAULT_DATA = {
  headline: "From audience to action",
  stages: [
    { title: "Awareness", subtitle: "Build reach and visibility", supportingLabel: "Discovery", icon: "awareness" },
    { title: "Consideration", subtitle: "Deepen engagement and intent", supportingLabel: "Build interest", icon: "engagement" },
    { title: "Action", subtitle: "Drive the campaign objective", supportingLabel: "Campaign objective", icon: "action" },
  ],
  bottomJourney: ["Discovery", "Evaluation", "Decision", "Action"],
  endGoalLabel: "Campaign objective",
};

/** Normalize editable funnel data into three bounded, accessible text stages. */
export const normalizeStrategyFunnelData = (data, fallbackData = DEFAULT_DATA) => {
  const supplied = object(data);
  const fallback = object(fallbackData);
  const stages = DEFAULT_DATA.stages.map((standard, index) => {
    const entry = object(list(supplied.stages)[index]);
    const base = object(list(fallback.stages)[index]);
    const icon = firstText(entry.icon, base.icon, standard.icon);
    return {
      title: text(entry.title, 64) || text(base.title, 64) || standard.title,
      subtitle: text(entry.subtitle, 200) || text(base.subtitle, 200) || standard.subtitle,
      supportingLabel: text(entry.supportingLabel, 72) || text(base.supportingLabel, 72) || standard.supportingLabel,
      icon: ICONS.has(icon) ? icon : standard.icon,
    };
  });
  const journey = (value) => [...new Set(list(value).slice(0, 12).map((entry) => text(entry, 32)).filter(Boolean))].slice(0, 4);
  const suppliedJourney = journey(supplied.bottomJourney);
  const fallbackJourney = journey(fallback.bottomJourney);
  return {
    headline: text(supplied.headline, 100) || text(fallback.headline, 100) || DEFAULT_DATA.headline,
    stages,
    bottomJourney: suppliedJourney.length ? suppliedJourney : fallbackJourney.length ? fallbackJourney : [...DEFAULT_DATA.bottomJourney],
    endGoalLabel: text(supplied.endGoalLabel, 72) || text(fallback.endGoalLabel, 72) || DEFAULT_DATA.endGoalLabel,
  };
};

// Resolve one declared field at a time. A concrete goal wins over a generic
// objective or incidental words in a creative narrative. Mixed KPI lists may
// legitimately contain awareness, engagement, and a specific end action.
const classifyGoal = (value) => {
  // Excluded outcomes are not campaign goals. If a clause mixes positive and
  // negative intent, prefer a generic fallback over guessing the negation scope.
  const candidate = text(value, 1000).toLowerCase().replace(/[’‘]/g, "'").replace(/[_-]/g, " ")
    .split(/[;.!?]|\b(?:but|however)\b/)
    .filter((clause) => !/\b(?:no|not|without|avoid\w*|exclud\w*|non|cannot|never)\b|\b(?:don|won|isn|aren|doesn|shouldn|can|couldn|wouldn|mustn|hasn|haven|didn)'t\b/.test(clause))
    .join(" ").trim();
  if (!candidate) return "";
  if (/\b(ticket(?:s)? (?:sales?|purchas\w*|booking\w*)|sell tickets|buy tickets|box office)\b/.test(candidate)) return "ticket";
  if (candidate === "lead" || /\b(leads|lead generation|lead gen|inquir\w*|book(?:ing)? (?:a |more )?demos?|demo (?:request|booking)s?|sales qualified|mqls?|sqls?)\b/.test(candidate)) return "lead";
  if (/\b(sign ?ups?|register|registration|subscrib\w*|subscriptions?|enroll\w*)\b/.test(candidate)) return "signup";
  if (/\b(purchas\w*|sales?|e ?commerce|checkout|orders?|roas|retail conversion)\b/.test(candidate)) return "purchase";
  if (/\b(attendance|attend|rsvp|footfall|store visits?)\b/.test(candidate)) return "attendance";
  if (/\b(traffic|website visits?|site visits?|landing page views?|click throughs?|clicks|ctr)\b/.test(candidate)) return "traffic";
  if (/\b(engagement|engage|interaction|interact|participation|video views?)\b/.test(candidate)) return "engagement";
  if (/\b(awareness|reach|visibility|discovery|recognition|recall|impressions?|cpm)\b/.test(candidate)) return "awareness";
  return "";
};

const GOALS = {
  ticket: {
    middleTitle: "Engagement", middle: "Build familiarity, anticipation, and intent to attend.",
    title: "Ticket sales", subtitle: "Turn interest into ticket purchases.", icon: "ticket", end: "Ticket purchase",
    journey: ["Discovery", "Interest", "Decision", "Tickets"],
  },
  attendance: {
    title: "Attendance", subtitle: "Turn audience interest into attendance.", icon: "ticket", end: "Attendance",
    journey: ["Discovery", "Interest", "Plan a visit", "Attend"],
  },
  purchase: {
    middle: "Reinforce value and build confidence in the offer.",
    title: "Purchase", subtitle: "Help high-intent shoppers take the next step to purchase.", icon: "purchase", end: "Purchase",
    journey: ["Discovery", "Evaluation", "Decision", "Purchase"],
  },
  lead: {
    middle: "Educate qualified prospects and build trust.",
    title: "Inquiry", subtitle: "Turn qualified interest into inquiries and leads.", icon: "lead", end: "Qualified leads",
    journey: ["Discovery", "Education", "Evaluation", "Inquiry"],
  },
  signup: {
    middle: "Explain the value and build confidence to take part.",
    title: "Sign-up", subtitle: "Turn interest into registrations and sign-ups.", icon: "signup", end: "Sign-ups",
    journey: ["Discovery", "Interest", "Decision", "Sign-up"],
  },
  traffic: {
    middle: "Build interest and give audiences a reason to explore.",
    title: "Visit", subtitle: "Encourage interested audiences to visit the campaign destination.", icon: "traffic", end: "Campaign traffic",
    journey: ["Discovery", "Interest", "Explore", "Visit"],
  },
  engagement: {
    middleTitle: "Engagement", middle: "Deepen interaction and familiarity with relevant messaging.",
    title: "Participation", subtitle: "Encourage meaningful interaction with the campaign.", icon: "engagement", end: "Audience engagement",
    journey: ["Discovery", "Interest", "Connection", "Engagement"],
  },
  awareness: {
    middleTitle: "Familiarity", middle: "Reinforce the message and help audiences recognize the brand.",
    title: "Recall", subtitle: "Make the campaign memorable and strengthen audience awareness.", icon: "awareness", end: "Reach and awareness",
    journey: ["Discovery", "Recognition", "Familiarity", "Recall"],
  },
};

const hasFunnelCopy = (value) => {
  const data = object(value);
  return Boolean(firstText(data.headline, data.endGoalLabel) ||
    list(data.stages).some((stage) => firstText(object(stage).title, object(stage).subtitle, object(stage).supportingLabel)) ||
    list(data.bottomJourney).some((entry) => text(entry)));
};

/**
 * Build a strategy explainer from named public fields, without changing the
 * strategy artifact or making performance, reach, or activation claims.
 * Returns null when neither strategy content nor a declared goal is present.
 */
export const buildStrategyFunnel = (value, { proposalPlanning = null, formatText = (copy) => copy } = {}) => {
  const campaign = object(value);
  const sections = object(campaign.sections);
  // The page passes its already-validated proposal, the same record that owns
  // its planning story. Do not resurrect legacy template narrative in that mode.
  const proposal = proposalPlanning ? object(proposalPlanning) : null;
  const overview = proposal ? {} : object(sections.strategyOverview || campaign.strategyOverview);
  const creative = proposal ? {} : object(sections.creativeMessaging || campaign.creativeMessaging);
  const explicit = hasFunnelCopy(sections.strategyFunnel) ? sections.strategyFunnel : campaign.strategyFunnel;
  const goalFields = [campaign.campaignGoal, campaign.goal, campaign.conversionGoal, campaign.endAction, campaign.primaryKpi, campaign.objective];
  const explicitGoal = goalFields.slice(0, 4).map(classifyGoal).find(Boolean);
  const objectiveGoal = classifyGoal(campaign.objective);
  const kpiGoal = classifyGoal(campaign.primaryKpi);
  const conversionGoals = ["ticket", "purchase", "lead", "signup", "attendance"];
  // A conversion KPI can clarify a broad objective, but proxy metrics such as
  // CTR / video views must not replace an explicit Ticket Sales / Awareness goal.
  const declaredGoal = proposal
    ? list(object(proposal.kpis).primary).map(classifyGoal).find(Boolean) || ""
    : explicitGoal ||
    (conversionGoals.includes(objectiveGoal) ? objectiveGoal : "") ||
    (conversionGoals.includes(kpiGoal) ? kpiGoal : "") || objectiveGoal || kpiGoal;
  // Only explicit goal language / calls to action may determine the destination.
  // Server-generated overview prose can contain legacy industry boilerplate.
  const goal = declaredGoal || [creative.objectiveContext, creative.callToAction].map(classifyGoal).find(Boolean) || "";
  const profile = { ...(GOALS[goal] || {}) };
  const destinationText = (proposal ? list(object(proposal.kpis).primary) : goalFields)
    .find((entry) => classifyGoal(entry) === goal) || creative.callToAction || creative.objectiveContext;
  if (goal === "lead" && /\bdemos?\b/i.test(text(destinationText))) {
    Object.assign(profile, {
      title: "Demo request", subtitle: "Turn qualified interest into demo requests.", end: "Demo requests",
      journey: ["Discovery", "Education", "Evaluation", "Request a demo"],
    });
  }
  const positioning = firstText(overview.positioningStatement, overview.overview, overview.summary);
  const mediaNarrative = firstText(overview.strategyLogic, overview.crossPlatformStrategy, overview.channelStrategyRationale, overview.distributionStrategy, overview.mediaMixRationale);
  const isCompatibleCopy = (entry) => {
    // Source-coverage and page-population notices explain the artifact, not
    // the audience journey. Leave those in their existing page sections.
    if (/keep(?:ing)? the page|page (?:populat\w*|render\w*)|(?:metrics|data|source coverage).{0,30}\b(?:pending|unavailable|missing)|title[- ]reference sources/i.test(text(entry, 1000))) return false;
    const inferred = classifyGoal(entry);
    return !goal || !inferred || !["ticket", "purchase", "lead", "signup", "attendance", "traffic"].includes(inferred) || inferred === goal;
  };
  // The creative themes run hook, middle, call to action. The middle stage is
  // carried by a theme between the two (a hook only when there is no other),
  // never by the call to action. A theme whose copy is the ad itself ("…
  // Get Tickets") names the stage; its words stay in Creative Messaging.
  const themes = list(creative.themes).map(object).filter((entry) =>
    firstText(entry.title, entry.copy, entry.description) &&
    [entry.title, entry.copy, entry.description].every(isCompatibleCopy));
  const theme = themes.find((entry) => classifyCreativeThemeRole(entry) === CREATIVE_THEME_ROLE.CONSIDERATION) ||
    themes.find((entry) => classifyCreativeThemeRole(entry) === CREATIVE_THEME_ROLE.AWARENESS) || {};
  const stageCopy = (entry) => (isCreativeExecutionCopy(entry) ? "" : entry);
  // The backend's template summary names the campaign by a connected source's
  // title and lists channels: it describes no stage.
  const summary = isTemplateCreativeSummary(creative.summary) ? "" : creative.summary;
  // Who the campaign is introduced to: people, or the audiences interested in
  // a saved interest, never a raw targeting value (campaignJourneySemantics).
  const audienceIntent = resolveJourneyAudience(campaign, { proposalPlanning: proposal });
  const audienceLabel = text(audienceIntent.label, 72);
  const themeLabel = text(theme.title, 64);
  const narrative = firstText(...[stageCopy(theme.copy), stageCopy(theme.description), summary, mediaNarrative].filter(isCompatibleCopy));
  const hasContext = goalFields.some((entry) => text(entry)) ||
    list(object(proposal?.kpis).primary).some((entry) => text(entry)) ||
    firstText(creative.callToAction, creative.objectiveContext, positioning, narrative,
      ...[creative.summary, mediaNarrative].filter(isCompatibleCopy),
      ...themes.flatMap((entry) => [entry.title, entry.copy, entry.description]), audienceLabel);
  if (!hasContext && !hasFunnelCopy(explicit)) return null;

  // The name the journey introduces: a film's saved title or its advertiser,
  // a brand's advertiser, never a flight label such as "2026 Theatrical
  // Release" or "Christmas CTV 2026" (campaignJourneySemantics).
  const { name: subject, film } = resolveCampaignJourneyIdentity(campaign);
  let introduction = DEFAULT_DATA.stages[0].subtitle;
  if (subject && audienceIntent.phrase) introduction = `Introduce ${subject} to ${audienceIntent.phrase}.`;
  else if (subject) introduction = `Build ${film ? "title " : ""}awareness for ${subject}.`;
  else if (audienceIntent.phrase || audienceLabel) introduction = `Build reach and visibility with ${audienceIntent.phrase || audienceLabel}.`;
  // Positioning is useful context when no named subject/audience is available.
  else if (positioning && isCompatibleCopy(positioning)) introduction = positioning;

  const derived = {
    headline: "From audience to action",
    stages: [
      { ...DEFAULT_DATA.stages[0], subtitle: introduction, supportingLabel: audienceLabel || "Discovery" },
      {
        ...DEFAULT_DATA.stages[1],
        title: profile.middleTitle || "Consideration",
        subtitle: narrative || profile.middle || DEFAULT_DATA.stages[1].subtitle,
        supportingLabel: themeLabel || "Build interest",
      },
      {
        title: film && goal === "ticket" ? "Attendance" : profile.title || "Action",
        subtitle: film && goal === "ticket" ? "Support ticket purchase and theater attendance." : profile.subtitle || DEFAULT_DATA.stages[2].subtitle,
        supportingLabel: profile.end || "Campaign objective",
        icon: profile.icon || "action",
      },
    ],
    bottomJourney: profile.journey || DEFAULT_DATA.bottomJourney,
    endGoalLabel: profile.end || text(proposal
      ? list(object(proposal.kpis).primary).find((entry) => text(entry))
      : firstText(campaign.conversionGoal, campaign.endAction, campaign.campaignGoal, campaign.goal, campaign.objective), 72) || "Campaign objective",
  };
  const funnel = normalizeStrategyFunnelData(explicit, derived);
  // Keep the microsite's existing public-copy and category wording boundary
  // on every displayed string, including explicit editable funnel data. A
  // stage title the category wording leaves in lower case ("qualified
  // response") starts with a capital.
  return normalizeStrategyFunnelData({
    headline: formatText(funnel.headline),
    stages: funnel.stages.map((stage) => ({
      ...stage,
      title: sentenceCaseTitle(formatText(stage.title)),
      subtitle: formatText(stage.subtitle),
      supportingLabel: formatText(stage.supportingLabel),
    })),
    bottomJourney: funnel.bottomJourney.map((label) => formatText(label)),
    endGoalLabel: formatText(funnel.endGoalLabel),
  });
};
